// Helper function to escape XML special characters
function escapeXML(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// Helper function to convert scene title to slug
function slugify(text) {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
    .replace(/\-\-+/g, '-')         // Replace multiple - with single -
    .replace(/^-+/, '')             // Trim - from start of text
    .replace(/-+$/, '');            // Trim - from end of text
}

// Helper function to make URLs absolute
function makeAbsoluteUrl(url) {
  if (!url) return url;
  
  try {
    // Try to create URL object - if it succeeds, it's already absolute
    new URL(url);
    return url;
  } catch {
    // If URL creation fails, it's relative - make it absolute
    // Get the full path up to the last slash
    const basePath = window.location.pathname.substring(0, window.location.pathname.lastIndexOf('/') + 1);
    return new URL(url, window.location.origin + basePath).toString();
  }
}

// Helper function to create XML element with attributes
function createXMLElement(name, attributes = {}, children = '') {
  // Convert URLs in attributes to absolute URLs
  const processedAttributes = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (key.toLowerCase().includes('url')) {
      processedAttributes[key] = makeAbsoluteUrl(value);
    } else {
      processedAttributes[key] = value;
    }
  }
  
  const attrs = Object.entries(processedAttributes)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${key}="${escapeXML(String(value))}"`)
    .join(' ');
    
  if (children) {
    return `<${name}${attrs ? ' ' + attrs : ''}>${children}</${name}>`;
  }
  return `<${name}${attrs ? ' ' + attrs : ''}/>`;
}

// Generate scene XML
function generateSceneXML(scene) {
  const buildViewElement = (viewData, devices) => {
    if (!viewData) return "";
    return createXMLElement("view", {
      hlookat: viewData.hlookat,
      vlookat: viewData.vlookat,
      fov: viewData.fov,
      fovtype: "MFOV",
      fovmin: viewData.fovmin,
      fovmax: viewData.fovmax,
      limitview: "auto",
      ...(devices ? { devices } : {}),
    });
  };

  const viewElements = [
    buildViewElement(scene.view),
    buildViewElement(scene.mobileView, "mobile"),
  ].filter(Boolean);

  // Create image element
  // Try to get sphere images if available, otherwise fallback to cube
  let images = scene.images.filter(image => image.imageType === 'sphere');
  let imageElements;
  if (images.length > 0) {
    // get the highest quality sphere
    const highestQualitySphere = images.reduce((highest, current) => {
      return (current.tiledimagewidth > highest.tiledimagewidth) ? current : highest;
    }, images[0]);
    
    imageElements = createXMLElement('image', {}, 
      createXMLElement('sphere', {
        url: highestQualitySphere.url
      })
    );
  } else {
    images = scene.images.filter(image => image.imageType !== 'sphere');
    if (images.length === 0) {
        console.error('No images found for scene', scene.title);
        return;
    }
    imageElements = images.map(image => createXMLElement('image', {}, 
      createXMLElement('cube', {
        url: image.url
      })
    )).join('\n    ');
  }
  
  // Create scene element with all children
  return createXMLElement('scene', {
    id: scene.id,
    name: scene.name,
    title: scene.title,
    slug: slugify(scene.title),
    thumburl: scene.thumbUrl,
    burried: scene.burried || false
  }, `\n    ${viewElements.join("\n    ")}\n    ${imageElements}\n  `);
}

import embedXML from './embed.xml';

// Main function to generate embed XML
export function generateEmbedXML(scenes) {
  // Validate scenes array
  if (!Array.isArray(scenes) || scenes.length === 0) {
    throw new Error('No scenes provided or invalid scenes array');
  }

  // Generate XML for each scene
  const scenesXML = scenes
    .map(scene => generateSceneXML(scene))
    .join('\n  ');
  
  // Create the complete XML with proper XML declaration
  const xml = embedXML.replace('<include url="embed-scenes.xml" />', scenesXML);
  return xml;
}

// Function to create a Blob URL for the generated XML
export function createEmbedXMLUrl(scenes) {
  try {
    const xml = generateEmbedXML(scenes);
    const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error generating embed XML:', error);
    throw error;
  }
}

const POPUP360_NEXT_SCENE_LOOP = `<action name="nextscene_loop" type="Javascript"><![CDATA[
  var slideDirection = (args[1] == 1) ? 0 : 180;
  if (krpano.get('xml.scene') != null && krpano.get('scene.count') > 1) {
    var currentSceneIndex = krpano.get('scene[' + krpano.get('xml.scene') + '].index');
    var sceneCount = krpano.get('scene.count');
    var newSceneIndex = currentSceneIndex + parseInt(args[1]);
    var lastSceneIndex = sceneCount - 1;
    function findNextVisibleScene(index, direction) {
      while (krpano.get('scene[' + index + '].burried') == "true") {
        index += direction;
        if (index < 0) index = lastSceneIndex;
        if (index > lastSceneIndex) index = 0;
      }
      return index;
    }
    if (newSceneIndex < 0) newSceneIndex = lastSceneIndex;
    if (newSceneIndex > lastSceneIndex) newSceneIndex = 0;
    newSceneIndex = findNextVisibleScene(newSceneIndex, parseInt(args[1]));
    krpano.call("loadscene('" + krpano.get('scene[' + newSceneIndex + '].name') + "', null, MERGE, SLIDEBLEND(1.0, " + slideDirection + ", 0.5, linear));");
  }
]]></action>`;

/** Minimal krpano XML for map POI 360 popup — no tour.xml or embed.xml. */
export function generatePopup360XML(scenes, { startIndex = 0 } = {}) {
  if (!Array.isArray(scenes) || scenes.length === 0) {
    throw new Error('No scenes provided or invalid scenes array');
  }

  const scenesXML = scenes
    .map((scene) => generateSceneXML(scene))
    .filter(Boolean)
    .join('\n  ');

  if (!scenesXML) {
    throw new Error('No valid scene XML generated');
  }

  const safeStartIndex = Math.min(Math.max(0, startIndex), scenes.length - 1);

  return `<?xml version="1.0" encoding="UTF-8"?>
<krpano version="1.20" title="" logkey="false" showerrors="false" debugmode="false">
  <display autofullscreen="false" />
  <contextmenu fullscreen="false" versioninfo="false" />
  <control
    mouse="drag"
    touch="drag"
    zoomtocursor="false"
    zoomoutcursor="false"
    draginertia="0.1"
    dragfriction="0.9"
  />

  <action name="startup" autorun="onstart">
    if(startscene === null OR !scene[get(startscene)], copy(startscene, scene[get(startIndex)].name); );
    loadscene(get(startscene), null, MERGE);
  </action>

  ${POPUP360_NEXT_SCENE_LOOP}

  ${scenesXML}
</krpano>`;
}

/** Blob URL for popup 360 krpano viewer. Caller must revoke when done. */
export function createPopup360XMLUrl(scenes, options = {}) {
  try {
    const xml = generatePopup360XML(scenes, options);
    const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error generating popup 360 XML:', error);
    throw error;
  }
}
